import dayjs from 'dayjs';
import { saveAs } from 'file-saver';

import { GetDebugPanelDataResponse } from '../types/debug-panel-data';
import { GetDebugPanelDataDto } from '../types/dto';
import {
  GetAllIntercultureForms,
  IntercultureFormComplete,
} from '../types/types';
import { IntercultureValues } from '../types/values';

import {
  FORM_STATUS,
  Filters,
  formatFilters,
  handleRequest,
  I18n,
  MultisectActionDto,
  privateClient,
} from '@data-access';

export const intercultureApi = {
  getAllForms: async ({
    crop,
    filters,
  }: {
    crop: string;
    filters: Filters;
  }) => {
    return privateClient
      .get(`nitrogen/${crop}/`, {
        searchParams: {
          ...formatFilters(filters),
        },
      })
      .json<GetAllIntercultureForms>();
  },
  getValues: async ({ crop }: { crop: string }) => {
    const values = await privateClient
      .get(`nitrogen/${crop}/values/`)
      .json<IntercultureValues>();

    const variety = values.variety.map((v) => ({
      label: v.name,
      value: v.id.toString(),
      data: v,
    }));

    const previous_crop = values.previous_crop.map((pc) => ({
      label: pc.name,
      value: pc.id.toString(),
      data: pc,
    }));

    const interculture_crop = values.interculture_crop.map((ic) => ({
      label: ic.name,
      value: ic.id.toString(),
      data: ic,
    }));

    return {
      raw: values,
      options: {
        variety,
        previous_crop,
        interculture_crop,
      },
    };
  },
  getResult: ({ crop, id }: { crop: string; id: number }) => {
    return privateClient
      .get(`nitrogen/${crop}/${id}/result/`)
      .json<IntercultureFormComplete>();
  },
  getTypes: async () => {
    const res = await handleRequest<
      {
        name: string;
        parent: {
          id: number;
          name: string;
          i18n: I18n;
          code: string;
          unit: string;
        };
      }[]
    >(privateClient.get(`nitrogen/types/`).json());

    return res;
  },
  getTuto: async (crop: string) => {
    // api/nitrogen/wheat/tutorial
    const res = await handleRequest<{
      id: number;
      page: string;
      file: string;
    }>(privateClient.get(`nitrogen/${crop}/tutorial/`).json());

    return res;
  },
  getDebugPanelData: async (params: GetDebugPanelDataDto) => {
    // api debug_panel/common/154/yield_instance/125/
    const res = await handleRequest<GetDebugPanelDataResponse>(
      privateClient
        .get(
          `debug_panel/common/${params.fieldId}/yield_instance/${params.yieldId}/`,
          {
            searchParams: {
              layer: params.layer,
            },
          },
        )
        .json(),
    );

    // const sowingDate = dayjs()
    //   .set('date', 25)
    //   .set('month', 5)
    //   .toDate()
    //   .getTime();
    const sowingDate = new Date(res.seed_date).getTime()
    const harvestDate = new Date(res.harvest_date).getTime();
    const previousHarvestDate = new Date(res.previous_crop_harvest_date).getTime();

    const graphData = res.graph.data
      ? Object.entries(res.graph.data)
          .map(([key, value]) => ({
            ...value,
            date: new Date(value.date).getTime(),
            max: [value.min, value.max],
            id: key,
          }))
          .sort((a, b) => {
            const dateA = dayjs(a.date);
            const dateB = dayjs(b.date);
            if (dateA.isBefore(dateB)) {
              return -1;
            }
            if (dateA.isAfter(dateB)) {
              return 1;
            }
            return 0;
          })
      : [];

    return { ...res, graphData, sowingDate, harvestDate, previousHarvestDate };
  },
  createForm: ({ crop, data }: { crop: string; data: unknown }) => {
    return privateClient
      .post(`nitrogen/${crop}/`, {
        json: data,
      })
      .json();
  },
  updateForm: ({
    crop,
    id,
    data,
  }: {
    crop: string;
    id: number;
    data: Partial<IntercultureFormComplete>;
  }) => {
    return privateClient
      .patch(`nitrogen/${crop}/${id}/`, {
        json: data,
      })
      .json();
  },
  updateManyForms: ({
    crop,
    id,
    data,
  }: {
    crop: string;
    id: number[];
    data: Partial<IntercultureFormComplete>;
  }) => {
    return privateClient
      .patch(`nitrogen/${crop}/bulk_update/`, {
        json: {
          ...data,
          id,
        },
      })
      .json();
  },
  deleteOne: async (args: { id: number; crop: string }) => {
    const res = await handleRequest(
      privateClient.delete(`nitrogen/${args.crop}/${args.id}/`).json(),
    );

    return res;
  },
  deleteMany: async (data: MultisectActionDto & { crop: string }) => {
    const res = await handleRequest(
      privateClient
        .delete(`nitrogen/${data.crop}/bulk_delete/`, {
          json: data,
        })
        .json(),
    );

    return res;
  },

  createForcedResult: (data: CreateForcedResultDto) => {
    return privateClient.post('forced_results/', { json: data }).json();
  },

  // synthesis
  exportSynthesis: async (data: MultisectActionDto & { crop: string }) => {
    const res = await handleRequest<{
      id: 89;
      status: FORM_STATUS;
      instance_id: 89;
    }>(
      privateClient
        .post(`nitrogen/${data.crop}/prep_synthesis/`, {
          json: data,
        })
        .json(),
    );

    return res;
  },
  downloadExport: async (opts: { id: number; crop: string }) => {
    const res = await handleRequest(
      privateClient.post(`nitrogen/${opts.crop}/synthesis/`, {
        json: {
          instance_id: opts.id,
        },
      }),
    );

    // Extract filename from header
    const filename = res.headers
      .get('content-disposition')
      ?.split(';')
      .find((n) => n.includes('filename='))
      ?.replace('filename=', '')
      .trim();

    const blob = await res.blob();

    saveAs(blob, filename);

    return res;
  },
};

interface CreateForcedResultDto {
  form: number;
  comment: string;
  forced_status: FORM_STATUS;
}
